'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import {
  TrendingUp,
  Trophy,
  ShieldCheck,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  ArrowRight,
  Activity,
} from 'lucide-react';
import { useLogin, authApi } from '@/sdk/shared/auth';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { getCookie, getBrokerageNameFromHostname } from '@/sdk/shared/auth/utils';
import { useGeneralSettings } from '@/sdk/admin/features/general_settings';
import GoogleSignInButton from '@/components/auth/GoogleSignInButton';

export default function ClientLoginPage() {
  const router = useRouter();
  const { login, isLoading, error, clearError } = useLogin();
  const { setAuth } = useAuth();

  const [tenantSlug, setTenantSlug] = useState<string | undefined>(undefined);
  const { generalSettings } = useGeneralSettings(tenantSlug);

  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      // Extract root brokerage name from the main domain (e.g. legacyglobalbank from devlegacyglobalbank.legacyglobalbank.com)
      const hostBrokerageName = getBrokerageNameFromHostname() || undefined;

      const slug =
        params.get('tenantSlug') ||
        params.get('slug') ||
        getCookie('tenant_slug') ||
        localStorage.getItem('last_tenant_slug') ||
        hostBrokerageName;

      if (slug) {
        setTenantSlug(slug);
      }
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      toast.error('Please enter your email address first.');
      return;
    }
    setIsResending(true);
    try {
      await authApi.resendVerification(formData.email);
      toast.success('Verification email sent! Please check your inbox.');
      setShowResend(false);
    } catch (err: any) {
      const errorMsg = err?.error || err?.message || 'Failed to resend verification email. Please try again.';
      toast.error(errorMsg);
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowResend(false);

    try {
      const response = await login({ ...formData, loginAs: 'client', tenantSlug });
      toast.success('Login successful!');

      setAuth(response);

      if (response.client?.tenant?.slug) {
        localStorage.setItem('last_tenant_slug', response.client.tenant.slug);
      }

      const client = response.client;
      const termsAccepted = Boolean(
        client?.termsAndConditionsAccepted ||
        client?.terms_and_conditions_accepted ||
        client?.termsAndConditions ||
        (client as any)?.terms_and_conditions
      );
      const hasMissingFields = Boolean(response.accessGate?.missingFields && response.accessGate.missingFields.length > 0);
      const hasAllFields = Boolean(
        !hasMissingFields &&
        (response.accessGate?.allowed || response.accessGate?.missingFields?.length === 0) &&
        client &&
        (client.firstName || client.first_name) &&
        (client.lastName || client.last_name) &&
        (client.dateOfBirth || client.dob || client.date_of_birth) &&
        client.city &&
        client.country &&
        termsAccepted
      );

      if ((response.accessGate && !response.accessGate.allowed) || !hasAllFields) {
        if (response.accessGate?.missingFields && response.accessGate.missingFields.length > 0) {
          toast.info('Please complete your registration details before accessing the Client Portal.');
          router.push('/register');
          return;
        }
      }

      window.location.href = '/client/overview';
    } catch (err: any) {
      const errorMessage = err?.error || 'Login failed. Please verify your credentials.';
      toast.error(errorMessage);
      if (errorMessage.toLowerCase().includes('verify your email')) {
        setShowResend(true);
      }
    }
  };

  const handleGoogleSuccess = async (idToken: string) => {
    setIsGoogleLoading(true);
    try {
      const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
      const urlSlug = urlParams?.get('slug') || urlParams?.get('tenantSlug');
      const effectiveSlug =
        tenantSlug ||
        urlSlug ||
        (typeof window !== 'undefined' ? localStorage.getItem('last_tenant_slug') || getCookie('tenant_slug') : undefined) ||
        undefined;

      const response = await authApi.loginWithGoogle(idToken, effectiveSlug);

      if (response.isNewUser) {
        if (typeof window !== 'undefined') {
          sessionStorage.setItem(
            'pending_google_user',
            JSON.stringify({
              ...response.googleUser,
              idToken,
            })
          );
        }
        toast.info('Google account verified. Please complete your registration.');
        const targetUrl = effectiveSlug
          ? `/register?google=true&slug=${encodeURIComponent(effectiveSlug)}`
          : '/register?google=true';
        router.push(targetUrl);
        return;
      }

      toast.success('Login successful via Google!');
      setAuth(response);

      if (response.client?.tenant?.slug) {
        localStorage.setItem('last_tenant_slug', response.client.tenant.slug);
      }

      const client = response.client;
      const termsAccepted = Boolean(
        client?.termsAndConditionsAccepted ||
        client?.terms_and_conditions_accepted ||
        client?.termsAndConditions ||
        (client as any)?.terms_and_conditions
      );
      const hasMissingFields = Boolean(response.accessGate?.missingFields && response.accessGate.missingFields.length > 0);
      const hasAllFields = Boolean(
        !hasMissingFields &&
        (response.accessGate?.allowed || response.accessGate?.missingFields?.length === 0) &&
        client &&
        (client.firstName || client.first_name) &&
        (client.lastName || client.last_name) &&
        (client.dateOfBirth || client.dob || client.date_of_birth) &&
        client.city &&
        client.country &&
        termsAccepted
      );

      if ((response.accessGate && !response.accessGate.allowed) || !hasAllFields) {
        if (response.accessGate?.missingFields && response.accessGate.missingFields.length > 0) {
          toast.info('Please complete your registration details before accessing the Client Portal.');
          router.push('/register');
          return;
        } else if (!hasAllFields) {
          toast.info('Please complete your registration details before accessing the Client Portal.');
          router.push('/register');
          return;
        }
      }

      window.location.href = '/client/overview';
    } catch (err: any) {
      const errorMessage = err?.error || 'Google Sign-In failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleGoogleFailure = (error: string) => {
    toast.error(error);
  };

  return (
    <div className="min-h-screen w-full flex bg-[#0B111E] text-foreground">
      {/* Left Section - Hero & Branding with login-page.svg */}
      <div className="hidden lg:flex lg:w-[48%] xl:w-[50%] bg-[#111827] flex-col justify-between relative overflow-hidden p-10 xl:p-14 border-r border-slate-800/60 select-none">
        {/* Subtle background ambient glow */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#2ED573]/5 rounded-full blur-3xl pointer-events-none" />

        {/* Top Branding / Logo */}
        <div className="relative z-10">
          <Link href="/" className="inline-flex items-center">
            {generalSettings?.logoUrl ? (
              <img
                src={generalSettings.logoUrl}
                alt="Logo"
                className="h-56 xl:h-50 max-w-[280px] object-contain drop-shadow-md"
              />
            ) : (
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-[#2ED573] to-[#10AC5E] flex items-center justify-center shadow-xl shadow-[#2ED573]/25">
                {/* <Activity className="w-8 h-8 text-[#0B111E]" /> */}
              </div>
            )}
          </Link>
        </div>

        {/* Middle Hero Content */}
        <div className="relative z-10 my-auto py-8">
          <div className="space-y-3">
            <h1 className="text-4xl xl:text-5xl font-extrabold text-white tracking-tight leading-[1.15]">
              Trade smarter.
              <br />
              <span className="text-[#2ED573]">Grow consistently.</span>
            </h1>
            <p className="text-slate-400 text-sm xl:text-base leading-relaxed max-w-md pt-2">
              Track your performance. Build your edge. Stay disciplined. Stay ahead.
            </p>
          </div>

          {/* 3 Feature Highlights matching mockup */}
          <div className="mt-10 space-y-5">
            {/* Feature 1 */}
            <div className="flex items-start gap-4 group">
              <div className="w-11 h-11 rounded-xl bg-[#2ED573]/10 border border-[#2ED573]/25 flex items-center justify-center shrink-0 text-[#2ED573] transition-transform duration-300 group-hover:scale-105 group-hover:bg-[#2ED573]/15">
                <TrendingUp className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm xl:text-[15px]">
                  Performance analytics
                </h3>
                <p className="text-slate-400 text-xs xl:text-sm mt-0.5">
                  Deep insights into your trading journey.
                </p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="flex items-start gap-4 group">
              <div className="w-11 h-11 rounded-xl bg-[#2ED573]/10 border border-[#2ED573]/25 flex items-center justify-center shrink-0 text-[#2ED573] transition-transform duration-300 group-hover:scale-105 group-hover:bg-[#2ED573]/15">
                <Trophy className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm xl:text-[15px]">
                  Streaks & rewards
                </h3>
                <p className="text-slate-400 text-xs xl:text-sm mt-0.5">
                  Stay consistent and unlock exclusive rewards.
                </p>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="flex items-start gap-4 group">
              <div className="w-11 h-11 rounded-xl bg-[#2ED573]/10 border border-[#2ED573]/25 flex items-center justify-center shrink-0 text-[#2ED573] transition-transform duration-300 group-hover:scale-105 group-hover:bg-[#2ED573]/15">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm xl:text-[15px]">
                  Secure & reliable
                </h3>
                <p className="text-slate-400 text-xs xl:text-sm mt-0.5">
                  Your data and funds are protected.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Graphic with login-page.svg */}
        <div className="relative -mx-10 -mb-10 xl:-mx-14 xl:-mb-14 overflow-hidden pointer-events-none">
          <img
            src="/assets/login-page.svg"
            alt="Trading Chart Visualization"
            className="w-full h-auto object-cover select-none"
          />
        </div>
      </div>

      {/* Right Section - Client Login Form */}
      <div className="w-full lg:w-[52%] xl:w-[50%] min-h-screen bg-white dark:bg-[#0E1526] flex flex-col justify-center items-center p-6 sm:p-10 lg:p-12">
        <div className="w-full max-w-[420px]">
          {/* Mobile Logo Header */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            {generalSettings?.logoUrl ? (
              <img
                src={generalSettings.logoUrl}
                alt="Logo"
                className="h-34 max-w-[240px] object-contain drop-shadow-sm"
              />
            ) : (
              <div className="w-12 h-12 rounded-2xl bg-[#2ED573] flex items-center justify-center shadow-lg shadow-[#2ED573]/20">
                <Activity className="w-7 h-7 text-[#0B111E]" />
              </div>
            )}
          </div>

          {/* Form Header */}
          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              Welcome back!
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1.5">
              Your trading journey continues here.
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Field */}
            <div className="space-y-1.5">
              <label
                htmlFor="email"
                className="block text-xs font-semibold text-slate-700 dark:text-slate-300"
              >
                Email Address <span className="text-emerald-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <Mail className="h-4 w-4" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  disabled={isLoading || isGoogleLoading}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50/70 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-xl text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2ED573]/30 focus:border-[#2ED573] transition-all disabled:opacity-60"
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-1.5">
              <label
                htmlFor="password"
                className="block text-xs font-semibold text-slate-700 dark:text-slate-300"
              >
                Password <span className="text-emerald-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <Lock className="h-4 w-4" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter password"
                  disabled={isLoading || isGoogleLoading}
                  className="w-full pl-10 pr-11 py-2.5 bg-slate-50/70 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-xl text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2ED573]/30 focus:border-[#2ED573] transition-all disabled:opacity-60"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  tabIndex={-1}
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 dark:border-slate-700 text-[#2ED573] focus:ring-[#2ED573] accent-[#2ED573]"
                />
                <span className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                  Remember me
                </span>
              </label>

              <Link
                href={tenantSlug ? `/forgot-password?slug=${encodeURIComponent(tenantSlug)}` : '/forgot-password'}
                className="text-xs font-semibold text-[#2ED573] hover:text-[#25b560] hover:underline transition-colors"
              >
                Forgot Password?
              </Link>
            </div>

            {showResend && (
              <div className="p-3 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/30 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                <span className="text-xs text-emerald-800 dark:text-emerald-300 font-medium">
                  Didn't receive the email, or link expired?
                </span>
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={isResending}
                  className="text-xs font-bold text-[#2ED573] hover:text-[#25b560] transition-colors flex items-center gap-1 disabled:opacity-60 whitespace-nowrap self-start sm:self-auto"
                >
                  {isResending ? 'Sending...' : 'Resend Verification'}
                </button>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || isGoogleLoading}
              className="w-full h-11 mt-2 bg-[#2ED573] hover:bg-[#28be66] active:scale-[0.99] text-white font-bold rounded-xl text-sm shadow-md shadow-[#2ED573]/20 flex items-center justify-center gap-2 transition-all cursor-pointer disabled:opacity-70"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin text-white" />
              ) : (
                <>
                  <span>Login</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200 dark:border-slate-800" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white dark:bg-[#0E1526] px-3 text-slate-400">
                or continue with
              </span>
            </div>
          </div>

          {/* Google Sign In Button */}
          <div className="w-full">
            <GoogleSignInButton
              onSuccess={handleGoogleSuccess}
              onFailure={handleGoogleFailure}
              text="Sign in with Google"
              theme="outline"
              shape="rectangular"
            />
          </div>

          {/* Sign Up Footer */}
          <div className="mt-8 text-center">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Don&apos;t have an account?{' '}
              <Link
                href={tenantSlug ? `/register?slug=${encodeURIComponent(tenantSlug)}` : '/register'}
                className="font-bold text-[#2ED573] hover:underline"
              >
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
